import { gql, useMutation } from "@apollo/client";
import type { FirebaseMessagingTypes } from "@react-native-firebase/messaging/lib";
import * as Notifications from "expo-notifications";
import { useCallback, useEffect, useState } from "react";
import { NativeModules, Platform } from "react-native";
import { colors } from "../../theme";

export const MUTATION = gql`
  mutation CreateMembershipPushNotificationToken($token: String!, $deviceType: PushNotificationDeviceType!) {
    createMembershipPushNotificationToken(token: $token, deviceType: $deviceType) {
      success
    }
  }
`;

let messaging: {
  (): FirebaseMessagingTypes.Module;
};

if (NativeModules.RNFBAppModule) {
  const { default: defaultMessaging } = require("@react-native-firebase/messaging");
  messaging = defaultMessaging;
}

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: false,
    shouldSetBadge: false,
  }),
});

async function getPushNotificationToken() {
  if (!messaging) return;

  let token;
  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  if (existingStatus !== "granted") {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus === "granted") {
    token = await messaging().getToken();

    if (Platform.OS === "android") {
      Notifications.setNotificationChannelAsync("default", {
        name: "default",
        importance: Notifications.AndroidImportance.MAX,
        vibrationPattern: [0, 250, 250, 250],
        lightColor: colors.red.darkest,
      });
    }
  }

  return token;
}

export default function usePushNotifications() {
  const [sendMutation] = useMutation(MUTATION);
  const [registered, setRegistered] = useState<boolean | undefined>(undefined);

  const registerPushNotifications = useCallback(async () => {
    try {
      const token = await getPushNotificationToken();
      if (token) sendMutation({ variables: { token, deviceType: Platform.OS.toUpperCase() } });
    } finally {
      return setRegistered(true);
    }
  }, []);

  async function applyNotificationStatus() {
    const { status } = await Notifications.getPermissionsAsync();
    if (status === "granted") {
      registerPushNotifications();
      setRegistered(true);
    } else {
      setRegistered(false);
    }
  }

  useEffect(() => {
    if (!registered || !messaging) return;

    const unsubscribe = messaging().onTokenRefresh((token) =>
      sendMutation({ variables: { token, deviceType: Platform.OS.toUpperCase() } })
    );

    return () => {
      unsubscribe();
    };
  }, [registered]);

  return {
    registerPushNotifications,
    registered,
    applyNotificationStatus,
  };
}
